/* eslint-disable react/no-unused-state */ // TODO: investigate if the state is needed, e.g. to trigger re-rendering

import React from 'react';
import PropTypes from 'prop-types';
import detectIt from 'detect-it';
import styled from 'styled-components';

import { bemGenerator, noop, animationUtils } from '@prisjakt/ui-base';
import ArrowIconRightMDI from 'mdi-react/ChevronRightIcon';
import ArrowIconLeftMDI from 'mdi-react/ChevronLeftIcon';

import './horizontal-scroll.scss';

const ArrowIconRight = styled(ArrowIconRightMDI).attrs(p => ({
  color: p.theme.icons.quaternary,
}))``;
const ArrowIconLeft = styled(ArrowIconLeftMDI).attrs(p => ({
  color: p.theme.icons.quaternary,
}))``;

const LOAD_MORE_ITEMS_LIMIT = 3;
const TRANSITION_TIME_MAX = 400;
const TRANSITION_TIME_MIN = 100;
const SHADOW_OVERLAY = 20;
const styles = bemGenerator('pj-ui-horizontal-scroll', [
  'inner',
  'arrows',
  'left-arrow',
  'right-arrow',
  'list',
  'item',
  'loader',
]);

const mockedDiv = {
  scrollLeft: 0,
  clientWidth: 0,
};

class HorizontalScroll extends React.Component {
  constructor(props) {
    super(props);

    this.scrollTimeout = undefined;

    this.outerContainer = mockedDiv;
    this.scrollContainer = mockedDiv;
    this.innerContainer = mockedDiv;

    this.state = {
      mounted: false,
      touching: false,
      leftShadow: false,
      rightShadow: true,
      clientWidth: undefined,
      rightArrowVisible:
        props.showArrows && React.Children.count(props.children) > 1,
      leftArrowVisible: false,
      itemWidth: 0,
    };
  }

  componentDidUpdate(prevProps) {
    const { showArrows, itemHint, itemWidth } = this.props;
    const { count } = React.Children;

    // Need to update arrow states when new children has arrived
    if (
      showArrows &&
      count(this.props.children) !== count(prevProps.children)
    ) {
      const { scrollLeft, clientWidth } = this.scrollContainer;
      const itemsLeft = this.getItemsLeft(scrollLeft + clientWidth);

      this.toggleArrows(scrollLeft, itemsLeft);
    }

    if (
      prevProps.itemHint !== itemHint ||
      (itemHint && prevProps.itemWidth !== itemWidth)
    ) {
      this.calculateWidth();
    }
  }

  resizeContainer() {
    this.setState({
      clientWidth: 0,
    });

    const { clientWidth } = this.outerContainer;

    this.setState({
      clientWidth,
    });
  }

  componentDidMount() {
    const { itemHint } = this.props;
    const pagination = this.getPagination();
    const { clientWidth, scrollLeft } = this.outerContainer;

    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      clientWidth,
      mounted: true,
    });

    if (itemHint) {
      this.calculateWidth();
    }

    if (pagination.current === 1 && pagination.pages === 1) {
      this.toggleArrows(
        scrollLeft,
        this.getItemsLeft(scrollLeft + clientWidth)
      );
    }

    this.resizeEvent = () => {
      this.resizeContainer();
    };

    window.addEventListener('resize', this.resizeEvent);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeEvent);

    this.setState({
      mounted: false,
    });
  }

  calculateWidth() {
    const { clientWidth } = this.scrollContainer;
    const { itemWidth } = this.props;

    if (!clientWidth) {
      return;
    }

    const space = clientWidth - itemWidth / 2;
    const spread = space / itemWidth;
    const items = Math.round(spread);
    const newWidth = itemWidth * (spread / items);

    this.setState({
      itemWidth: newWidth,
    });
  }

  togglePage(direction) {
    const { scrollLeft } = this.scrollContainer;
    const { current, itemsPerPage } = this.getPagination(scrollLeft);

    let goToPage = current - 1;

    switch (direction) {
      case 'prev':
        goToPage -= 1;
        break;
      case 'next':
        goToPage += 1;
        break;
      default:
        return;
    }

    this.scrollTo({
      from: scrollLeft,
      to: goToPage * itemsPerPage * this.getItemWidth(),
    });

    this.handleScroll();
  }

  getItemWidth() {
    return this.state.itemWidth || this.props.itemWidth;
  }

  getItemsLeft(scrollLeft) {
    const itemsLeft = Math.floor(scrollLeft / this.getItemWidth());
    return Math.max(
      0,
      React.Children.count(this.props.children) - 1 - itemsLeft
    );
  }

  getPagination(scrollLeft = 0) {
    const itemWidth = this.getItemWidth();
    const itemsPerPage = Math.floor(
      this.scrollContainer.clientWidth / itemWidth
    );
    const numberOfPages = Math.max(
      Math.floor(React.Children.count(this.props.children) / itemsPerPage),
      1
    );

    return {
      itemsPerPage,
      current: Math.floor(
        (scrollLeft + this.scrollContainer.clientWidth) /
          itemWidth /
          itemsPerPage
      ),
      pages: numberOfPages,
    };
  }

  toggleArrows(scrollLeft, itemsLeft) {
    const leftArrowVisible = Math.floor(scrollLeft / this.getItemWidth()) > 0;
    const rightArrowVisible =
      (itemsLeft !== 0 &&
        this.props.maxItems > React.Children.count(this.props.children)) ||
      itemsLeft;

    if (this.state.rightArrowVisible !== rightArrowVisible) {
      this.setState({
        rightArrowVisible,
      });
    }

    if (this.state.leftArrowVisible !== leftArrowVisible) {
      this.setState({
        leftArrowVisible,
      });
    }
  }

  handleScroll() {
    const { scrollWidth, scrollLeft, clientWidth } = this.scrollContainer;
    const scrollRight = scrollWidth - (clientWidth + scrollLeft);

    const { maxItems, onNeedData, loading, showArrows } = this.props;
    const { leftShadow, rightShadow } = this.state;
    const totalChilds = React.Children.count(this.props.children);
    const itemsLeft = this.getItemsLeft(scrollLeft + clientWidth);

    const needMoreData =
      onNeedData &&
      totalChilds <= maxItems - 1 &&
      !loading &&
      itemsLeft <= LOAD_MORE_ITEMS_LIMIT;

    // Trigger more data callback if callback is registered and items left are under limit
    if (needMoreData) onNeedData();

    if (showArrows) this.toggleArrows(scrollLeft, itemsLeft);

    if (
      (!leftShadow && scrollLeft >= SHADOW_OVERLAY) ||
      (leftShadow && scrollLeft < SHADOW_OVERLAY)
    ) {
      this.setState({
        leftShadow: !leftShadow,
      });
    }

    if (
      (!rightShadow && scrollRight > SHADOW_OVERLAY) ||
      (rightShadow && scrollRight < SHADOW_OVERLAY)
    ) {
      this.setState({
        rightShadow: !rightShadow,
      });
    }
  }

  handleTouchDown() {
    this.setState({
      touching: true,
    });
  }

  handleTouchEnd() {
    this.setState({
      touching: false,
    });
  }

  scrollTo({ from, to }) {
    if (from === to) return;

    const { scrollContainer } = this;
    const distanceNormalize = Math.abs(to - from) / TRANSITION_TIME_MIN;
    const duration = Math.max(
      TRANSITION_TIME_MIN,
      Math.min(TRANSITION_TIME_MAX, TRANSITION_TIME_MIN * distanceNormalize)
    );

    animationUtils.tween({
      from,
      to,
      duration,
      onUpdate: value => {
        scrollContainer.scrollLeft = Math.round(value);
      },
    });
  }

  getScrollWidth() {
    if (!this.state.mounted) return '100%';

    const itemWidth = this.getItemWidth();
    const items = React.Children.count(this.props.children);
    const clientWidth =
      this.scrollContainer && this.scrollContainer.clientWidth;

    return Math.max(
      clientWidth || 0,
      itemWidth * items + (this.props.loading ? 100 : 0)
    );
  }

  render() {
    const itemWidth = this.getItemWidth();
    const { children, showArrows } = this.props;
    const {
      leftShadow,
      rightShadow,
      leftArrowVisible,
      rightArrowVisible,
    } = this.state;

    return (
      <div
        ref={div => {
          this.outerContainer = div || mockedDiv;
        }}
        className={styles.block.withModifier({
          'left-shadow': leftShadow,
          'right-shadow': rightShadow,
        })}
      >
        <div
          ref={div => {
            this.scrollContainer = div || mockedDiv;
          }}
          onScroll={() => this.handleScroll()}
          onTouchStart={() => this.handleTouchDown()}
          onTouchEnd={() => this.handleTouchEnd()}
          className={styles.inner}
        >
          {showArrows && (
            <div className={styles.arrows}>
              <button
                type="button"
                onClick={() => this.togglePage('prev')}
                className={styles['left-arrow'].withModifier({
                  hidden: !leftArrowVisible,
                })}
              >
                <ArrowIconLeft />
              </button>
              <button
                type="button"
                onClick={() => this.togglePage('next')}
                className={styles['right-arrow'].withModifier({
                  hidden: !rightArrowVisible,
                })}
              >
                <ArrowIconRight />
              </button>
            </div>
          )}
          <ul
            ref={div => {
              this.innerContainer = div || mockedDiv;
            }}
            className={styles.list}
            style={{
              width: this.getScrollWidth(),
            }}
          >
            {React.Children.map(children, (child, i) => (
              <li
                key={i}
                className={styles.item}
                style={{ flex: `0 0 ${itemWidth}px` }}
              >
                {child}
              </li>
            ))}
            {this.props.loading && (
              <li
                className={styles.loader}
                style={{ lineHeight: `${this.props.loadingHeight}px` }}
              >
                <i className="fa fa-circle-o-notch fa-spin fa-2x fa-fw" />
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  }
}

HorizontalScroll.propTypes = {
  itemWidth: PropTypes.number.isRequired,
  loadingHeight: PropTypes.number,
  onNeedData: PropTypes.func,
  loading: PropTypes.bool,
  showArrows: PropTypes.bool,
  maxItems: PropTypes.number,
  itemHint: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

HorizontalScroll.defaultProps = {
  loading: false,
  loadingHeight: 250,
  itemHint: false,
  maxItems: Infinity,
  showArrows: ['hybrid', 'mouseOnly'].includes(detectIt.deviceType),
  onNeedData: noop,
};

export default HorizontalScroll;
